'use client';
import React from 'react';
import { useIsUserLoggedIn } from './hooks/useIsUserLoggedIn';
import { Modal } from '@benzinga/core-ui';
import { BenzingaContext } from './context';

export type LoginRegisterEventsTypes =
  | 'close'
  | 'already_logged_in'
  | 'login_succeed'
  | 'login_failed'
  | 'register_succeed'
  | 'start_trial_succeed'
  | 'register_failed';

interface Props {
  onEvent: (data: { type: LoginRegisterEventsTypes; next?: string }) => void;
  modalMode: boolean;
}

export const LoginRegister: React.FC<Props> = props => {
  const bzContext = React.useContext(BenzingaContext);
  const iFrameRef = React.useRef<HTMLIFrameElement | null>(null);
  const isLoggedIn = useIsUserLoggedIn();
  const allowedOrigins = React.useMemo(
    () => [
      bzContext.iFrameLoginURL.origin,
      'https://www.benzinga.com',
      'https://pro.benzinga.com',
      'local.benzinga.com:9000',
      'local.benzinga.com:3000',
    ],
    [bzContext.iFrameLoginURL.origin],
  );

  React.useEffect(() => {
    const handle = (event: { origin: string; data: { type: LoginRegisterEventsTypes; next?: string } }) => {
      if (allowedOrigins.includes(event.origin)) {
        const data = typeof event.data === 'string' ? { type: event.data } : event.data;
        switch (data.type) {
          case 'already_logged_in':
          case 'register_succeed':
          case 'start_trial_succeed':
          case 'login_succeed':
            props.onEvent(event.data);
            props.onEvent({ type: 'close' });
            break;
          case 'login_failed':
          case 'register_failed':
            props.onEvent(event.data);
            break;
        }
      }
    };
    window.addEventListener('message', handle);
    return () => window.removeEventListener('message', handle);
  }, [allowedOrigins, bzContext.iFrameLoginURL.origin, props]);

  const onClose = React.useCallback(() => props.onEvent({ type: 'close' }), [props]);

  const loginURL = React.useMemo(() => {
    const url = new URL(bzContext.iFrameLoginURL.toString());
    url.searchParams.set('iframe', 'true');
    url.searchParams.set('loginnext', bzContext.loginNextURL.toString());
    url.searchParams.set('registernext', bzContext.loginRegisterURL.toString());
    return url.toString();
  }, [bzContext.iFrameLoginURL, bzContext.loginNextURL, bzContext.loginRegisterURL]);

  if (isLoggedIn) {
    return null;
  }

  const login = (
    <iframe
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      frameBorder="0"
      ref={iFrameRef}
      src={loginURL}
      style={{ height: '100%', width: '100%' }}
      title="Login"
    ></iframe>
  );

  if (props.modalMode) {
    return (
      <Modal fullScreen={true} onClose={onClose} size={{ coverScreen: true }} title={'login'}>
        {login}
      </Modal>
    );
  } else {
    return login;
  }
};
